import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import { AuthCallback, Mfa, ResetPassword } from "@/pages/AuthPages";
import Onboarding from "@/pages/Onboarding";
import Profile from "@/pages/Profile";
import { Tasks, Offers, Practice, PracticeDetail, Insights } from "@/pages/Personal";
import Copilot from "@/pages/Copilot";
import Dashboard from "@/pages/Dashboard";
import Interviews from "@/pages/Interviews";
import SearchRules from "@/pages/SearchRules";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Studio, { DocumentView } from "@/pages/Studio";
import Tracker from "@/pages/Tracker";
import Settings, { Notifications } from "@/pages/Settings";
import AdminOverview from "@/pages/admin/Overview";
import CommandCenter from "@/pages/admin/CommandCenter";
import AdminUsers from "@/pages/admin/Users";
import AdminSystem from "@/pages/admin/System";
import AdminJobs from "@/pages/admin/Jobs";
import AdminAudit from "@/pages/admin/Audit";
import { AdminAccess, AdminCommunications, AdminGovernance, AdminIncidents } from "@/pages/admin/Records";
import { AdminConsole } from "@/pages/admin/shared";
import AdminProviders from "@/pages/admin/Providers";
import AdminRoutes from "@/pages/admin/Routes";
import AdminUsage from "@/pages/admin/Usage";
import AdminSources from "@/pages/admin/Sources";
import AdminPayments from "@/pages/admin/Payments";
function AdminGuard() {
  const {
    me,
    aal
  } = useAuth();
  if (me?.role !== "super_admin" && !me?.admin_permissions?.length) return <Navigate to="/jobs" replace />;
  if (aal.current !== "aal2") return <Navigate to="/mfa" replace />;
  return <AdminConsole />;
}
export default function App() {
  return <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route element={<AppShell />}>
                <Route path="/copilot" element={<Copilot />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/practice" element={<Practice />} />
                <Route path="/practice/:id" element={<PracticeDetail />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/interviews" element={<Interviews />} />
                <Route path="/rules" element={<SearchRules />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/:id" element={<JobDetail />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/studio" element={<Studio />} />
                <Route path="/studio/:id" element={<DocumentView />} />
                <Route path="/tracker" element={<Tracker />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/mfa" element={<Mfa />} />
                <Route path="/admin" element={<AdminGuard />}>
                  <Route index element={<CommandCenter />} />
                  <Route path="platform" element={<AdminOverview />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="access" element={<AdminAccess />} />
                  <Route path="system" element={<AdminSystem />} />
                  <Route path="jobs" element={<AdminJobs />} />
                  <Route path="incidents" element={<AdminIncidents />} />
                  <Route path="communications" element={<AdminCommunications />} />
                  <Route path="governance" element={<AdminGovernance />} />
                  <Route path="providers" element={<AdminProviders />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="routes" element={<AdminRoutes />} />
                  <Route path="usage" element={<AdminUsage />} />
                  <Route path="sources" element={<AdminSources />} />
                  <Route path="audit" element={<AdminAudit />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>;
}
